import _extends from "@babel/runtime/helpers/extends";
import React from "react";
import styled, { css, useTheme } from "styled-components";
import { genericColorCombination, resolveAnimations } from "~/lib/resolvers";
import Spinner from "~/components-shared/Spinner";
const Wrapper = styled.button`
  display: ${_ref => {
  let {
    $display
  } = _ref;
  return $display;
}};
  align-items: center;
  border-radius: ${_ref2 => {
  let {
    $rounded,
    theme
  } = _ref2;
  return $rounded ? "2em" : theme.shape.borderRadius.primary;
}};
  ${_ref3 => {
  let {
    $fontSize,
    theme
  } = _ref3;
  return $fontSize && `font-size: ${$fontSize * theme.typography.fontStep}rem`;
}};
  ${_ref4 => {
  let {
    $lineHeight,
    theme
  } = _ref4;
  return $lineHeight && `line-height: ${$lineHeight * theme.typography.fontStep}rem`;
}};
  text-align: ${_ref5 => {
  let {
    $textAlign
  } = _ref5;
  return $textAlign;
}};
  font-weight: ${_ref6 => {
  let {
    theme
  } = _ref6;
  return theme.typography.fontWeight.medium;
}};
  background-color: ${_ref7 => {
  let {
    $color,
    theme
  } = _ref7;
  return genericColorCombination(theme, $color).background;
}};
  background-image: ${_ref8 => {
  let {
    $color,
    theme
  } = _ref8;
  return genericColorCombination(theme, $color).backgroundImage;
}};
  color: ${_ref9 => {
  let {
    $color,
    $loading,
    theme
  } = _ref9;
  return $loading ? genericColorCombination(theme, $color).background : genericColorCombination(theme, $color).color;
}};
  border: 0;
  ${_ref10 => {
  let {
    $s,
    theme
  } = _ref10;
  return theme.spacing.resolve(theme, $s);
}};
  ${_ref11 => {
  let {
    $fullWidth
  } = _ref11;
  return $fullWidth && "width: 100%";
}};
  ${_ref12 => {
  let {
    $maxWidth
  } = _ref12;
  return $maxWidth && `max-width: ${$maxWidth}`;
}};
  position: relative;
  cursor: ${_ref13 => {
  let {
    disabled
  } = _ref13;
  return disabled ? "initial" : "pointer";
}};
  z-index: 1;
  -webkit-tap-highlight-color: transparent;
  ${_ref14 => {
  let {
    $outline,
    $color,
    theme
  } = _ref14;
  return $outline && theme.shadows.outline(theme, $outline, genericColorCombination(theme, $color).focus);
}}
  ${_ref15 => {
  let {
    $shadow,
    theme
  } = _ref15;
  return $shadow && `box-shadow: ${theme.shadows.large}`;
}};
  ${_ref16 => {
  let {
    $pulse,
    $color,
    theme
  } = _ref16;
  return $pulse && resolveAnimations.pulse("buttonPulse", theme, $color);
}}
  ${_ref17 => {
  let {
    disabled
  } = _ref17;
  return disabled && "opacity: 0.5";
}};
  transition: background-color 200ms ease-out, color 200ms ease-out, box-shadow 200ms ease-out;

  &:before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    transition: background-color 200ms ease-out;
    background-color: transparent;
    border-radius: ${_ref18 => {
  let {
    $rounded,
    theme
  } = _ref18;
  return $rounded ? "2em" : theme.shape.borderRadius.primary;
}};
    z-index: -1;
  }

  ${_ref19 => {
  let {
    disabled
  } = _ref19;
  return !disabled && css`
      &:hover:before {
        background-color: ${_ref20 => {
    let {
      theme
    } = _ref20;
    return theme.colors.action.hover;
  }};
      }
    `;
}}

  ${_ref21 => {
  let {
    $loading,
    $rounded,
    $color,
    theme
  } = _ref21;
  return $loading && css`
      &:after {
        content: "";
        position: absolute;
        width: 100%;
        height: 100%;
        top: 0;
        left: 0;
        background-image: ${genericColorCombination(theme, $color).backgroundImage};
        background-color: ${genericColorCombination(theme, $color).background};
        border-radius: ${$rounded ? "3rem" : theme.shape.borderRadius.primary};
      }
    `;
}}
`;
const Span = styled.span`
  flex-grow: 1;
  color: inherit;
  display: inline-block;
  font-family: ${_ref22 => {
  let {
    theme
  } = _ref22;
  return theme.typography.fontFamily.secondary;
}};
  visibility: ${_ref23 => {
  let {
    hidden
  } = _ref23;
  return hidden ? "hidden" : "visible";
}};
`;
const StyledSpinner = styled(Spinner)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1;
`;
const Button = _ref24 => {
  let {
    s = {},
    elemBefore,
    elemAfter,
    color = "primary",
    textAlign = "center",
    fontSize = 4,
    lineHeight,
    fullWidth = false,
    shadow = false,
    display = "flex",
    rounded = false,
    disabled = false,
    loading = false,
    pulse = false,
    outline = false,
    maxWidth = "initial",
    type = "button",
    children,
    ...rest
  } = _ref24;
  const theme = useTheme();
  return /*#__PURE__*/React.createElement(Wrapper, _extends({
    $s: {
      px: 4,
      py: 3,
      ...s
    },
    $color: color,
    $shadow: shadow,
    $textAlign: textAlign,
    $fullWidth: fullWidth,
    $fontSize: fontSize,
    $lineHeight: lineHeight,
    $rounded: rounded,
    $display: display,
    $maxWidth: maxWidth,
    type: type,
    disabled: disabled || loading,
    $loading: loading,
    $outline: outline,
    $pulse: pulse
  }, rest), elemBefore && elemBefore, /*#__PURE__*/React.createElement(Span, {
    hidden: loading
  }, children), elemAfter && elemAfter, loading && /*#__PURE__*/React.createElement(StyledSpinner, {
    scale: fontSize * 1.5,
    color: genericColorCombination(theme, color).color
  }));
};
export default Button;